import React from 'react'
import { createRoot } from 'react-dom/client'
import { TranslationContainer } from 'react-i18n-lite'
import { BrowserRouter } from 'react-router-dom'
import { Provider as ReduxProvider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { QueryClient, QueryClientProvider } from 'react-query'
import 'bootstrap/dist/css/bootstrap.css'
import 'bootstrap/dist/js/bootstrap.js'
import 'react-toastify/dist/ReactToastify.min.css'

import configureStore from 'state/store'
import apiAsync from 'infrastructure/shared/api/api-async'
import validateConfigUrls from 'infrastructure/shared/utils/validateConfigUrls'
import ApplicationContainer from 'infrastructure/Application/Container'
import ResizeDetector from 'shared/components/ResizeDetector'
import 'shared/assets/css/fonts.css'
import 'shared/assets/css/default.css'
import 'shared/assets/css/toastify.css'

import locales from './locales'

import reportWebVitals from './reportWebVitals'
import { ThemeProvider } from 'styled-components'
import { defaultTheme, YesErrorTracking } from '@yes.technology/react-toolkit'
import sanitizeSW from 'infrastructure/ServiceWorkerWrapper/utils/sanitizeServiceWorker'

const reduxStoreAndPersistor = configureStore()

validateConfigUrls(window)
apiAsync.setConfig(window.apiUrls)
apiAsync.setDispatch(reduxStoreAndPersistor.store.dispatch)

YesErrorTracking({
  service: 'sentry',
  publicKey: window.sentryDNS,
  traceTargets: window.trackingUrls
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false
    }
  }
})

const root = createRoot(document.getElementById('root') as HTMLElement)

window.addEventListener('message', (event) => {
  const { type, originalSender, sharedState } = event.data

  if (type === 'patchSharedState' && originalSender?.source) {
    const frames = document.getElementsByName(originalSender?.source)

    if (frames.length === 0) return

    const iframe = frames[0] as HTMLIFrameElement
    iframe.contentWindow?.postMessage({ type, sharedState }, '*')
  }
})

root.render(
  <React.StrictMode>
    <ThemeProvider theme={defaultTheme}>
      <ReduxProvider store={reduxStoreAndPersistor.store}>
        <PersistGate
          loading={null}
          persistor={reduxStoreAndPersistor.persistor}
        >
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <ResizeDetector>
                <TranslationContainer locales={locales} defaultLanguage='pt-BR'>
                  <ApplicationContainer />
                </TranslationContainer>
              </ResizeDetector>
            </BrowserRouter>
          </QueryClientProvider>
        </PersistGate>
      </ReduxProvider>
    </ThemeProvider>
  </React.StrictMode>
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()

// registerServiceWorker()
sanitizeSW()
